import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { connect } from 'react-redux';
import SinglePDFReportList from "../ErasureReport/Single/SinglePDFReportList";
import {copyToClipBoard, hasPermission} from "../../sc-utilities";
import * as actions from "../ErasureReport/Single/store/erasureReportActions";
import ReportRegenerate from "../../sc-productComponents/Report/ErasureReport/ReportRegenerate";
import {formatProductTypes} from "../../sc-admin/SystemCertificate/utils";
import {IconButton, Tooltip} from "@material-ui/core";
import {snackActions} from "../../sc-utilities/SnackBar";
import {CloudDownload, FindInPage, Link} from "@material-ui/icons";
import {download} from "../../sc-utilities/download.utils";
import {singleTableColumns} from "../ErasureReport/Single/columns/columns";
import {actionNavLink, CustomChip} from "../ErasureReport";
import config from "../../../config/config";
import {withStyles} from "@material-ui/core/styles";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        margin: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
});

const VerificationReport = props => {
    const tableRef = useRef();
    const { classes, permissions, translations, activeCustomerID, onRegenerateReport, onFetchProductType, activeCustomerName, columns } = props;

    const [regenerateAction, setRegenerateAction] = useState(0);
    const [regenerateModalState, setRegenerateModalState] = useState({ open: false, id: null });
    const [productTypes, setProductTypes] = useState(null);
    const [loading, setLoading] = useState({productTypes: false});

    const visible = columns && columns['VerificationReport'];

    const handleCloseDanger = useCallback(async (id = null) => {
        let result = await onRegenerateReport({ ReportID: id ? id : regenerateModalState.id, ActiveCustomerID: activeCustomerID });
        setRegenerateModalState({ open: false, id: null });

        if (result.data) {
            setRegenerateAction(regenerateAction + 1);
        }
    }, [regenerateAction, onRegenerateReport, activeCustomerID, setRegenerateModalState, setRegenerateAction, regenerateModalState.id]);

    const handleCloseSafe = () => {
        setRegenerateModalState({ open: false, id: null });
    };

    useEffect(() => {
        const fetchProductTypes = async () => {
            setLoading(prevState => {return {...prevState, productTypes: true}});
            let productTypesList = await onFetchProductType({ ActiveCustomerID: activeCustomerID });
            let list = [];
            let formattedProductTypes = productTypesList.reduce((obj, item) => Object.assign(obj, { [item.key]: formatProductTypes(item.label, translations) }), {});
            if (formattedProductTypes) {
                for (const [key] of Object.entries(formattedProductTypes)) {
                    list.push(key);
                }
            }
            setProductTypes({ lookup: formattedProductTypes, defaultFilter: list });
            setLoading(prevState => {return {...prevState, productTypes: false}});
        }
        if (!productTypes) {
            fetchProductTypes();
        }

        return () => { };
        // eslint-disable-next-line
    }, [productTypes, translations]);

    const singleColumns = useMemo(() => {

        const handleDownload = async id => {
            const objectURL = `${config.backend_domain}:${config.backend_port}/report/download?ReportID=${id}&ActiveCustomerID=${activeCustomerID}`;
            let exportDataSuccessful = await download(objectURL);
            tableRef.current && tableRef.current.onQueryChange();
            if (!exportDataSuccessful) {
                return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
            }
        };

        const createSingleAction = (rowData, translations) => {
            return <div>
                {hasPermission(permissions, 'erasure_report_download') && rowData.GeneratedStatus === 2 ?
                    <Tooltip title={translations["download_report"] || "Download report(s)"}>
                        <IconButton component='div' aria-label={translations["download_report"] || "Download report(s)"}
                                    onClick={() => handleDownload(rowData.ErasureReportID)}>
                            {<CloudDownload />}
                        </IconButton>
                    </Tooltip>
                    : null
                }
                {
                    actionNavLink(translations["product_details"] || "Product details", <FindInPage />, rowData, rowData.Product?.Type, activeCustomerID, activeCustomerName, permissions)
                }
                {
                    rowData?.Shared ?
                        <Tooltip title={translations["share_report"] || "Share report"}>
                            <IconButton component='div' aria-label={translations["share_report"] || "Share report"}
                                        onClick={() => {
                                            const details = JSON.parse(rowData?.Shared?.Details);
                                            copyToClipBoard(`${config.web}/shared?ID=${encodeURIComponent(rowData?.Shared?.Hash)}&ProductID=${details?.ProductID}&ProductType=${details?.ProductType}`)
                                            return snackActions.snackBarComponent(translations['copied_to_clipboard'] || `Copied to clipboard `, 'success');
                                        }}>
                                <Link />
                            </IconButton>
                        </Tooltip> : null
                }

            </div>
        };

        return singleTableColumns(productTypes, translations, visible, createSingleAction, CustomChip);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translations, visible, permissions, activeCustomerID, productTypes, activeCustomerName, loading.productTypes]);

    return (
        <div className={classes.root}>
            <SinglePDFReportList
                regenerateAction={regenerateAction}
                tableColumns={singleColumns}
                tableRef={tableRef}
                loading={loading}
                erasureReportType='verification-pdf'
            />
            {hasPermission(permissions, 'erasure_report_regenerate') ? (
                <ReportRegenerate
                    entityName={translations['generate_diagnose_report'] || 'Generate diagnose report'}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    regenerateModalState={regenerateModalState}
                />
            ) : null}
        </div>
    )
}

const mapDispatchToProps = dispatch => {
    return {
        onRegenerateReport: data => dispatch(actions.regenerateErasureReport(data)),
        onFetchProductType: params => dispatch(actions.listProductTypes(params)),
    };
}

const mapStateToProps = state => {
    return {
        activeCustomerName: state?.core?.login?.activeCustomer?.Name ?? null,
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(VerificationReport));
